import { type FC } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Text } from 'Text';
import { Button } from 'Button';
import FrontRoyalSpinner from 'FrontRoyalSpinner';
import { StorableImage } from 'StorableImage';
import { type StreamForStreamDashboard } from 'Lessons';
import { originalUrl } from 'hasResizableImages';
import { type ShareProvider } from '../StreamDashboard.types';
import { CompletionShareButtons } from './CompletionShareButtons';
import '../locales/streamDashboard/streamDashboard-en.json';

/*
    The completion screen: blue top half with the certificate, white bottom half with sharing, a continue
    bar pinned to the bottom. The title and GREAT JOB! are 32px, the top of the type scale, against the
    legacy 40px and 45px.
*/

type Props = {
    stream: StreamForStreamDashboard;
    isOffline: boolean;
    canShare: boolean;
    brandName: string;
    onShare: (provider: ShareProvider) => void;
    onContinue: () => void;
    isContinuing: boolean;
    certificateImageSrc: string | undefined;
};

export const StreamCompletedPage: FC<Props> = ({
    stream,
    isOffline,
    canShare,
    brandName,
    onShare,
    onContinue,
    isContinuing,
    certificateImageSrc,
}) => {
    const { t } = useTranslation('back_royal', { keyPrefix: 'streamDashboard.streamDashboard' });

    /*
        Three texts change for an exam, and a third case drops the invitation to share for a student who
        cannot share. Mirrors updateText in the old directive.
    */
    const completedText = stream.exam ? t('completion_exam_complete') : t('completion_course_complete');
    const continueText = stream.exam ? t('completion_return_to_dashboard') : t('completion_continue');
    const description = stream.exam
        ? t('completion_exam_is_complete')
        : t('completion_share_with_friend', { brandName });

    return (
        <div className="flex h-full flex-col text-center">
            <div className="flex h-1/2 flex-col items-center pt-2 sm:pt-7">
                {/* The certificate takes the space the two headings leave, and centres inside it. */}
                <div className="relative w-full flex-1">
                    {isOffline ? (
                        <StorableImage
                            src={originalUrl(stream.image) ?? ''}
                            alt=""
                            cacheForever
                            className="absolute start-1/2 top-1/2 max-h-[70%] max-w-[80%] -translate-x-1/2 -translate-y-1/2 rounded-full border-5 border-white shadow-[0_2px_4px_rgba(0,0,0,0.5)] rtl:translate-x-1/2"
                        />
                    ) : (
                        certificateImageSrc && (
                            <img
                                src={certificateImageSrc}
                                alt=""
                                className="absolute start-1/2 top-1/2 max-h-full max-w-[90%] -translate-x-1/2 -translate-y-1/2 py-5 rtl:translate-x-1/2"
                            />
                        )
                    )}
                </div>

                {/* Text applies its own colour and a semibold weight, so both are named here. */}
                <Text as="h2" weight="normal" className="mb-2 font-serif text-lg leading-5.5 text-white opacity-50">
                    {completedText}
                </Text>
                {/* Two lines are always reserved, whether the title needs them or not, and a longer
                    title is clipped. That is what `h1 { min-height: 66px; max-height: 88px; overflow:
                    hidden }` did, and it is what keeps the certificate above from filling the blue half:
                    the space the title does not use sits below it. Ours is 80px, two lines of our 40px
                    leading, against the legacy 88px of its 44px. */}
                <Text
                    as="h1"
                    className="m-0 h-20 overflow-hidden font-serif text-3xl font-bold leading-10 text-white max-sm:hidden"
                >
                    {stream.title.toUpperCase()}
                </Text>
            </div>

            <div className="flex h-1/2 flex-col items-center bg-white px-3 py-5 text-blue-completion sm:py-10">
                <Text as="div" className="mb-1.5 mt-2.5 text-3xl font-bold leading-10 text-blue-completion">
                    {t('completion_great_job')}
                </Text>

                {/* stream_completed.html put this inside the share block, so offline students and
                    those without share permission saw only GREAT JOB!, exam or not. */}
                {!isOffline && canShare && (
                    <Text
                        as="p"
                        weight="normal"
                        className="mb-5 max-w-[450px] text-sm leading-5.5 text-blue-completion sm:text-lg sm:leading-7.5"
                    >
                        {description}
                    </Text>
                )}

                {!isOffline && canShare && !stream.exam && <CompletionShareButtons onShare={onShare} />}
            </div>

            {/* A portal: .app-main-container (z-index 1) opens a stacking context below the nav (z-index 99).
                z-[9997] is $FRONT_ROYAL_FOOTER_Z_INDEX, below the modal at 9999. */}
            {createPortal(
                /* animate-bounce-in-up is the config's copy of the legacy bounceInUp keyframes, which the
                   footer attached to its continue button through footer-buttons-animation on ng-enter. */
                <div className="fixed inset-x-0 bottom-0 z-[9997] flex animate-bounce-in-up justify-center sm:bottom-6.25">
                    {isContinuing ? (
                        <FrontRoyalSpinner className="no-delay no-top-margin" />
                    ) : (
                        /* Flush on mobile. From sm: 550px wide, 4px radius, 25px above the window.
                           h-14 covers the 55px mobile nav. Button appends size classes in twMerge, so the heights need !. */
                        <Button
                            color="black"
                            onPress={onContinue}
                            className="!h-14 w-full rounded-none !bg-blue-completion sm:!h-12.5 sm:w-[550px] sm:rounded-2"
                        >
                            {continueText}
                        </Button>
                    )}
                </div>,
                document.body,
            )}
        </div>
    );
};
